import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

// Branded link domain (server/clientDomains.js) — the SAME card serves both
// surfaces (dual-surface rule): Admin → client detail / setup wizard (scope
// 'admin') and the client's own Settings → Branding (scope 'my'). Set a
// subdomain → add ONE CNAME → Pulse checks it reaches itself → the client's
// public links (forms, surveys, shared reports) read pulse.theirbrand.com/….
export default function LinkDomainCard({ entityId, scope = 'admin' }) {
  const [d, setD] = useState(null);
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');
  const hydrate = (v) => { setD(v); setDomain(v.domain || ''); };
  useEffect(() => { setD(null); setErr(''); api.getLinkDomain(entityId, scope).then(hydrate).catch((e) => setErr(e.message)); }, [entityId, scope]);
  if (err && !d) return <div style={{ fontSize: 12.5, color: 'var(--error,#ef4444)' }}>{err}</div>;
  if (!d) return <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Loading…</div>;

  const run = (fn) => { setBusy(true); setErr(''); fn().then(hydrate).catch((e) => setErr(e.message)).finally(() => setBusy(false)); };
  const save = () => { if (domain.trim()) run(() => api.saveLinkDomain(entityId, { domain: domain.trim() }, scope)); };
  const verify = () => run(() => api.verifyLinkDomain(entityId, scope));
  const remove = () => {
    if (window.confirm('Remove the branded link domain? New links go back to the Pulse address. Links already shared on this domain stop working once its DNS record is removed.')) run(() => api.deleteLinkDomain(entityId, scope));
  };
  const copy = async (v, k) => { try { await navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(''), 1500); } catch { /* ignore */ } };

  const chip = d.status === 'live'
    ? { label: '✓ Live — your links use this domain', color: '#15803d', bg: 'rgba(21,128,61,0.1)' }
    : d.status === 'unset' ? null
    : d.status === 'failed'
      ? { label: '✗ Stopped answering — links are back on the Pulse address', color: '#b91c1c', bg: 'rgba(185,28,28,0.1)' }
      : { label: 'Waiting for DNS — add the record below', color: '#b45309', bg: 'rgba(180,83,9,0.1)' };
  const input = { padding: '10px 12px', minHeight: 40, boxSizing: 'border-box', borderRadius: 8, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 14 };
  const btn = { ...input, cursor: 'pointer', fontWeight: 600 };
  const mono = { fontFamily: 'ui-monospace, monospace', fontSize: 12, wordBreak: 'break-all' };

  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 8 }}>
        Put <b>your own address</b> on the links Pulse creates for you — {d.covers.join(', ').toLowerCase()} — e.g. <code>pulse.yourbrand.com/ct/…</code> instead of {d.mainHost || 'the Pulse address'}. Use a subdomain so your website is untouched. Until it’s live, links keep using the Pulse address, and old links always keep working.
      </div>
      {scope === 'admin' && !d.enabled && (
        <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>The <b>Branded link domain</b> flag is off for this client (Admin → Product → Flags) — you can set it up, but links won’t move until it’s on.</div>
      )}
      {chip && <div style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, color: chip.color, background: chip.bg, borderRadius: 980, padding: '4px 12px', marginBottom: 8 }}>{chip.label}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="pulse.yourbrand.com" style={{ ...input, flex: '1 1 220px', minWidth: 0 }} aria-label="Link domain" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        <button onClick={save} disabled={busy || !domain.trim() || domain.trim() === d.domain} style={btn}>{busy ? '…' : (d.status === 'unset' ? 'Set up' : 'Save')}</button>
        {d.status !== 'unset' && <button onClick={verify} disabled={busy} style={btn}>↻ Check now</button>}
        {d.status !== 'unset' && <button onClick={remove} disabled={busy} style={{ ...btn, color: 'var(--error,#ef4444)' }}>Remove</button>}
      </div>
      {err && <div style={{ fontSize: 12, color: 'var(--error,#ef4444)', marginTop: 6 }}>{err}</div>}

      {d.status !== 'unset' && d.status !== 'live' && (
        <div style={{ marginTop: 10, border: '1px solid var(--hairline)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 12px', borderBottom: '1px solid var(--hairline)', lineHeight: 1.5 }}>
            Add this record at your DNS host (or send it to your IT team). Pulse checks every few minutes and switches your links over by itself — usually within an hour.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', alignItems: 'center', padding: '10px 12px', fontSize: 12.5 }}>
            <span style={{ fontWeight: 700 }}>CNAME</span>
            <span style={mono}>{d.domain}</span>
            <span style={{ color: 'var(--muted)' }}>→</span>
            <span style={{ ...mono, flex: '1 1 160px', minWidth: 0 }}>{d.cnameTarget}</span>
            <button onClick={() => copy(d.cnameTarget, 'target')} style={{ ...btn, padding: '6px 12px', fontSize: 12 }}>{copied === 'target' ? '✓ Copied' : 'Copy'}</button>
          </div>
          {d.lastError && <div style={{ fontSize: 12, color: '#b45309', padding: '0 12px 10px', lineHeight: 1.5 }}>Last check: {d.lastError}</div>}
          {!d.renderManaged && scope === 'admin' && (
            <div style={{ fontSize: 12, color: 'var(--muted)', padding: '0 12px 10px', lineHeight: 1.5 }}>
              Howler side: add <b>{d.domain}</b> as a custom domain on the Pulse service at <a href="https://dashboard.render.com" target="_blank" rel="noreferrer">dashboard.render.com</a> (Settings → Custom Domains) — or save a Render API key under Admin → Product → PRs &amp; deploys and Pulse adds it for you.
            </div>
          )}
        </div>
      )}
      {d.status === 'live' && (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
          New links now read <b>https://{d.domain}/…</b>. Links you shared before keep working on the Pulse address. Only your public pages open on this domain — anything else goes to Pulse.
        </div>
      )}
    </div>
  );
}
